import { useQuery } from '@tanstack/react-query';
import Navigation from '@/components/navigation';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BookOpen, Lightbulb, Globe, Star, ExternalLink } from 'lucide-react';
import { resolveContentImageUrl, stripHtml } from '@/lib/contentImage';

interface LearningContent {
  id: string;
  headline: string;
  subHeading?: string;
  bodyText?: string;
  imageUrl?: string;
  headerImage?: string;
  thumbnailUrl?: string;
  linkUrl: string;
  section?: string;
  isFeatured?: boolean;
  isPublished: boolean;
}

const Learning = () => {
  // Fetch learning content
  const { data: content, isLoading } = useQuery<LearningContent[]>({
    queryKey: ['/api/content/learning'],
    retry: false,
  });

  // Group content by section
  const featuredContent = content?.filter((c) => c.isFeatured && c.isPublished) || [];
  const knowTheApp = content?.filter((c) => c.section === 'know_the_app' && c.isPublished) || [];
  const learningContent =
    content?.filter((c) => (c.section === 'learning' || !c.section) && c.isPublished) || [];
  const blogs = content?.filter((c) => c.section === 'blogs' && c.isPublished) || [];
  const explainers = content?.filter((c) => c.section === 'explainer' && c.isPublished) || [];

  const displayLearning = learningContent;

  const ContentCard = ({ item }: { item: LearningContent }) => {
    const imageSrc = resolveContentImageUrl(item);
    return (
      <a
        href={item.linkUrl}
        target={item.linkUrl.startsWith('http') ? '_blank' : undefined}
        rel={item.linkUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
        className="block"
      >
        <Card className="overflow-hidden hover:shadow-lg transition-shadow border-lilac-200">
          {imageSrc ? (
            <div className="h-32 bg-lilac-100 overflow-hidden">
              <img src={imageSrc} alt={item.headline} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="h-24 bg-gradient-to-br from-lilac-200 to-purple-200 flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-purple-500" />
            </div>
          )}
          <div className="p-4">
            <h4 className="font-semibold text-deep-800 text-sm mb-1 font-kumbh line-clamp-2">
              {item.headline}
            </h4>
            {item.subHeading && (
              <p className="text-xs text-deep-500 mb-2 font-kumbh">{item.subHeading}</p>
            )}
            {item.bodyText && (
              <p className="text-xs text-deep-600 line-clamp-2 font-kumbh">
                {stripHtml(item.bodyText)}
              </p>
            )}
            {item.linkUrl.startsWith('http') && (
              <div className="flex items-center gap-1 mt-2 text-purple-600 text-xs">
                <ExternalLink className="w-3 h-3" />
                <span>Read more</span>
              </div>
            )}
          </div>
        </Card>
      </a>
    );
  };

  return (
    <div className="min-h-screen pb-24 bg-hydro-100">
      <div className="max-w-md mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-deep-800 mb-2 font-kumbh">Learning</h1>
          <p className="text-deep-500 font-kumbh">
            Tips, guides, and insights for better hydration
          </p>
        </div>

        {/* Featured Content */}
        {featuredContent.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Star className="w-5 h-5 text-purple-500" />
              <h2 className="font-semibold text-deep-800 font-kumbh">Featured</h2>
            </div>
            <Card className="p-6 bg-gradient-to-br from-lilac-200 to-purple-200 border-none">
              {featuredContent[0] && (
                <a href={featuredContent[0].linkUrl} target="_blank" rel="noopener noreferrer">
                  <div className="flex items-start gap-4">
                    <Lightbulb className="w-8 h-8 text-purple-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-lg text-deep-800 mb-2 font-kumbh">
                        {featuredContent[0].headline}
                      </h3>
                      {featuredContent[0].subHeading && (
                        <p className="text-sm text-deep-600 mb-2 font-kumbh">
                          {featuredContent[0].subHeading}
                        </p>
                      )}
                      {featuredContent[0].bodyText && (
                        <p className="text-sm text-deep-700 font-kumbh">
                          {featuredContent[0].bodyText}
                        </p>
                      )}
                    </div>
                  </div>
                </a>
              )}
            </Card>
          </div>
        )}

        {/* Content Tabs */}
        <Tabs defaultValue="learning" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-4 bg-lilac-100">
            <TabsTrigger value="learning" className="text-xs font-kumbh">
              Learning
            </TabsTrigger>
            <TabsTrigger value="know-app" className="text-xs font-kumbh">
              Know App
            </TabsTrigger>
            <TabsTrigger value="blogs" className="text-xs font-kumbh">
              Blogs
            </TabsTrigger>
            <TabsTrigger value="explainers" className="text-xs font-kumbh">
              Explainers
            </TabsTrigger>
          </TabsList>

          <TabsContent value="learning" className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {displayLearning.map((item) => (
                <ContentCard key={item.id} item={item as LearningContent} />
              ))}
            </div>
            {displayLearning.length === 0 && (
              <p className="text-center text-deep-500 py-8 font-kumbh">
                No learning content available yet.
              </p>
            )}
          </TabsContent>

          <TabsContent value="know-app" className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {knowTheApp.map((item) => (
                <ContentCard key={item.id} item={item} />
              ))}
            </div>
            {knowTheApp.length === 0 && (
              <Card className="p-6 bg-lilac-50 border-lilac-200">
                <p className="text-center text-deep-500 font-kumbh">App guides coming soon! 📱</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="blogs" className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {blogs.map((item) => (
                <ContentCard key={item.id} item={item} />
              ))}
            </div>
            {blogs.length === 0 && (
              <Card className="p-6 bg-lilac-50 border-lilac-200">
                <p className="text-center text-deep-500 font-kumbh">Blog posts coming soon! ✍️</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="explainers" className="space-y-4">
            <Card className="p-6 bg-gradient-to-br from-purple-100 to-lilac-100 border-none mb-4">
              <h3 className="font-semibold text-deep-800 mb-3 font-kumbh">Badges Explainer</h3>
              <div className="space-y-3 text-sm text-deep-700 font-kumbh">
                <div className="p-3 bg-white/60 rounded-lg">
                  <p className="font-medium text-purple-600 mb-1">Frequency Badges</p>
                  <p className="text-xs text-deep-600 mb-3">
                    Based on how consistently you respond to reminders — from Rhythm (0 misses) to
                    Flatline (7+ misses).
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      '/badges/frequency/rhythm.png',
                      '/badges/frequency/driven.png',
                      '/badges/frequency/steady.png',
                      '/badges/frequency/stray.png',
                      '/badges/frequency/broken.png',
                      '/badges/frequency/flatline.png',
                    ].map((src) => (
                      <img
                        key={src}
                        src={src}
                        alt="Frequency badge"
                        className="h-10 w-auto mx-auto object-contain"
                        loading="lazy"
                      />
                    ))}
                  </div>
                </div>
                <div className="p-3 bg-white/60 rounded-lg">
                  <p className="font-medium text-purple-600 mb-1">Volume Badges</p>
                  <p className="text-xs text-deep-600 mb-3">
                    Based on % of daily goal achieved — from H2nO? (≤50%) up to H2gOat (&gt;110%).
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      '/badges/volume/h2no.png',
                      '/badges/volume/h2ooops.png',
                      '/badges/volume/h2oh.png',
                      '/badges/volume/h2ah.png',
                      '/badges/volume/h2wow.png',
                      '/badges/volume/h2goat.png',
                    ].map((src) => (
                      <img
                        key={src}
                        src={src}
                        alt="Volume badge"
                        className="h-10 w-auto mx-auto object-contain"
                        loading="lazy"
                      />
                    ))}
                  </div>
                </div>
                <div className="p-3 bg-white/60 rounded-lg">
                  <p className="font-medium text-purple-600 mb-1">Time Badges</p>
                  <p className="text-xs text-deep-600">
                    Based on your active hours. From "Spark" (short sessions) to "God" (always on),
                    with Tank for longer days!
                  </p>
                </div>
              </div>
            </Card>
            <div className="grid grid-cols-2 gap-3">
              {explainers.map((item) => (
                <ContentCard key={item.id} item={item} />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Navigation />
    </div>
  );
};

export default Learning;
